import type { AuthorResponse } from '@shared/api-utils';
import { imagePathResolver } from '@shared/utils';
import { AuthorProfile } from '../AuthorProfile/AuthorProfile';
import { buildMagazinePageHref } from '../helpers/buildMagazinePageHref';
import type { MagazineArticle } from '../helpers/magazineCategory';
import { MagazineArticleGrid } from '../MagazineArticleGrid/MagazineArticleGrid';
import { MagazineAuthorPersonJsonLd } from '../MagazineAuthorPersonJsonLd/MagazineAuthorPersonJsonLd';
import { MagazineTrackMeta } from '../MagazineBody/MagazineTrackMeta';
import { MagazinePagination } from '../MagazinePagination/MagazinePagination';

type MagazineAuthorPageProps = {
  author: AuthorResponse;
  baseUrl: string;
  baseImgUrl: string;
  pageNumber?: number;
  articles: readonly MagazineArticle[];
  totalPages: number;
};

function MagazineAuthorPage({ articles, author, baseUrl, baseImgUrl, pageNumber = 1, totalPages }: MagazineAuthorPageProps) {
  const imageSrc = author.profilePicture?.filePath ? imagePathResolver(author.profilePicture.filePath, baseImgUrl) : undefined;

  return (
    <div data-testid="magazine-author-page">
      <MagazineTrackMeta container="Author Profile" provider={author.name} />
      <MagazineAuthorPersonJsonLd
        name={author.name}
        authorSlug={author.slug}
        baseUrl={baseUrl}
        baseImgUrl={baseImgUrl}
        description={author.seoInfo?.metaDescription}
        profilePicture={author.profilePicture}
      />

      <AuthorProfile className="mt-8" name={author.name} authorSlug={author.slug} bio={author.bio} imageSrc={imageSrc} showReadMoreLink={false} />

      <MagazineArticleGrid
        className="mt-8"
        articles={articles}
        baseImgUrl={baseImgUrl}
        currentPage={pageNumber}
        priorityFirstCard={pageNumber === 1}
        data-testid="magazine-author-articles"
      />

      <MagazinePagination
        className="mt-8"
        currentPage={pageNumber}
        totalPages={totalPages}
        getPageHref={page => buildMagazinePageHref({ type: 'author', authorSlug: author.slug }, page)}
      />
    </div>
  );
}

export { MagazineAuthorPage, type MagazineAuthorPageProps };
